import { allocationSummary } from "@/lib/roomAllocations";
import { isRichHtml } from "@/lib/richText";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { 
  Hotel, 
  Car, 
  Activity as ActivityIcon, 
  Utensils, 
  Plane,
  GripVertical,
  Trash2,
  Edit,
  MapPin,
  User,
  Calendar,
  History,
  ChevronDown,
  Package,
  MoreVertical,
  MessageSquare,
  Copy,
  Shield,
  Globe,
  CheckSquare,
  CalendarClock,
  Clock,
  Pencil as PencilIcon,
  Check as CheckIcon,
  Archive,
  X as XIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getCurrencySymbol, getCurrencyIcon } from "@/lib/currencyUtils";
import { calculateItemTotalCost, calculateAccommodationNights } from "@/lib/itineraryCostUtils";
import { baseItemCost, pricingTreatment } from "@/lib/quoteChoices";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO, addDays } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { EditItineraryItemDialog } from "./EditItineraryItemDialog";
import { SaveToInventoryDialog } from "./itinerary/SaveToInventoryDialog";
import { copyItineraryItem } from "@/lib/itineraryItemCopy";
import { formatEquivalents } from "@/lib/currencyConversion";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { RequestSupplierQuoteDialog } from "./RequestSupplierQuoteDialog";
import { groupItemsByDay } from "@/lib/dayGroupingUtils";
import {
  splitOngoingProgrammes,
  formatSessionDateSpan,
  formatSessionSummary,
  formatSessionTiming,
  totalSessionHours,
} from "@/lib/ongoingProgrammes";

import { splitInfrastructureFees } from "@/lib/infrastructureFees";
import { DayBanner } from "./itinerary/DayBanner";
import { SingleSupplementDialog } from "./itinerary/SingleSupplementDialog";
import { GroupLeaderDialog } from "./itinerary/GroupLeaderDialog";
import { computeGroupLeaderImpact, parseLeaderRoomOverrides } from "@/lib/groupLeaderUtils";
import { BatchDateBar } from "./itinerary/BatchDateBar";
import { useDatePickerDefault } from "@/hooks/itinerary-dates";
import { RealignDaysDialog, RealignChoice } from "./itinerary/RealignDaysDialog";
import { analyseProgrammeAlignment, formatProgrammeDate } from "@/lib/programmeAlignment";
import { DEFAULT_ITINERARY_SECTIONS, groupItemsBySection, parseItinerarySections, type ItineraryLayout } from "@/lib/itinerarySections";



type ItineraryItem = {
  id: string;
  title: string;
  custom_title: string | null;
  custom_description: string | null;
  item_type: "accommodation" | "transfer" | "activity" | "meal" | "flight" | "language_excursions" | "conference" | "tour" | "tours" | "custom" | "insurance" | "supplier" | "universal_fee";
  description: string | null;
  date: string | null;
  check_in: string | null;
  check_out: string | null;
  service_start?: string | null;
  service_end?: string | null;
  accommodation_type: string | null;
  occupancy_type: string | null;
  room_rate_id: string | null;
  location: string | null;
  cost: number | null;
  manual_price: number | null;
  quantity: number | null;
  supplier: string | null;
  notes: string | null;
  website_url?: string | null;
  sort_order: number | null;
  include_in_quote?: boolean;
  is_background_cost?: boolean;
  exclude_from_trade_discount?: boolean;
  manual_image_url: string | null;
  universal_fee_kind?: string | null;
  staff_categories?: string[] | null;
  room_guest_type?: string | null;
  staff_role_tags?: string[] | null;
  leader_excluded?: boolean | null;
  section_name?: string | null;
  onRequestQuote?: (itemId: string) => void;

};

interface ItineraryListProps {
  items: ItineraryItem[];
  leadId: string;
  currency?: string;
  paxCount?: number;
  leadStatus?: string;
  leadStartDate?: string | null;
}


const ITEM_ICONS = {
  accommodation: Hotel,
  transfer: Car,
  activity: ActivityIcon,
  meal: Utensils,
  flight: Plane,
  language_excursions: ActivityIcon,
  conference: ActivityIcon,
  tour: ActivityIcon,
  tours: ActivityIcon, // Backward compatibility
  custom: Package,
  insurance: Shield,
  supplier: Package,
  universal_fee: Package,
};

const ITEM_COLORS = {
  accommodation: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  transfer: "bg-green-500/10 text-green-500 border-green-500/20",
  activity: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  meal: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  flight: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  language_excursions: "bg-pink-500/10 text-pink-500 border-pink-500/20",
  conference: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  tour: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  tours: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20", // Backward compatibility
  custom: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  insurance: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  supplier: "bg-slate-500/10 text-slate-500 border-slate-500/20",
  universal_fee: "bg-slate-500/10 text-slate-500 border-slate-500/20",
};

function SortableItem({ item, onDelete, onEdit, onDuplicate, onToggleInclude, onToggleBackground, onToggleTradeDiscountExclusion, onAddStaffRooms, onSaveToInventory, savedAs, changes, isCancelled, isReadOnly, currency = "ZAR", baseCurrency = "ZAR", displayCurrency = "ZAR", convertCost, leaderActive = false, onToggleLeaderJoins, selectMode = false, isSelected = false, onToggleSelect }: { 
  item: ItineraryItem; 
  onDelete: (id: string) => void;
  onEdit: (item: ItineraryItem) => void;
  onDuplicate: (item: ItineraryItem) => void;
  onToggleInclude: (id: string, include: boolean) => void;
  onToggleBackground: (id: string, isBackground: boolean) => void;
  onToggleTradeDiscountExclusion: (id: string, excluded: boolean) => void;
  onAddStaffRooms: (item: ItineraryItem) => void;
  onSaveToInventory?: (item: ItineraryItem) => void;
  savedAs?: string | null;
  changes: any[];
  isCancelled?: boolean;
  isReadOnly?: boolean;
  currency?: string;
  baseCurrency?: string;
  displayCurrency?: string;
  convertCost?: (amount: number, from: string, to: string) => number;
  leaderActive?: boolean;
  onToggleLeaderJoins?: (id: string, joins: boolean) => void;
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string, selected: boolean) => void;
}) {


  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = ITEM_ICONS[item.item_type];
  const linkedId = (item as any).linked_option_item_id as string | null | undefined;
  const { data: linkedTitle } = useQuery({
    queryKey: ["linked-option-title", linkedId],
    enabled: !!linkedId,
    queryFn: async () => {
      const { data } = await supabase.from("itinerary_items").select("title, custom_title").eq("id", linkedId!).maybeSingle();
      return data ? (data.custom_title || data.title) : null;
    },
  });
  const { data: categoryNames } = useQuery({
    queryKey: ["supplier-category-names"],
    queryFn: async () => {
      const { data } = await supabase.from("supplier_categories").select("id, name");
      return Object.fromEntries((data || []).map((c: any) => [c.id, c.name])) as Record<string, string>;
    },
    staleTime: 5 * 60 * 1000,
  });
  
  // Use shared utility for cost calculation (handles accommodation nights correctly)
  const baseCost = calculateItemTotalCost(item);
  const totalCost = convertCost ? convertCost(baseCost, baseCurrency, displayCurrency) : baseCost;
  
  // Calculate nights for accommodation display
  const nights = item.item_type === 'accommodation' 
    ? calculateAccommodationNights(item.check_in, item.check_out) 
    : 0;
  
  const CurrencyIcon = getCurrencyIcon(displayCurrency);
  const currencySymbol = getCurrencySymbol(displayCurrency);
  const isIncluded = item.include_in_quote !== false;
  const isBackground = item.is_background_cost === true;
  const isStaffRoom = item.item_type === 'accommodation' && item.room_guest_type === 'staff';
  const isGuestAccommodation = item.item_type === 'accommodation' && item.room_guest_type !== 'staff';

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <Card className={`p-3 sm:p-4 hover:shadow-md transition-shadow ${!isIncluded ? 'opacity-50 border-dashed' : ''} ${isBackground ? 'border-dashed border-amber-500/40 bg-amber-500/5' : ''} ${isStaffRoom ? 'border-l-4 border-l-slate-500' : ''} ${selectMode && isSelected ? 'ring-2 ring-primary' : ''}`}>
        <div className="flex gap-2 sm:gap-3">
          <div className="flex flex-col items-center gap-2">
            {selectMode && (
              <div className="flex flex-col items-center gap-0.5 rounded-md border border-primary/40 bg-primary/5 px-1.5 py-1">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) => onToggleSelect?.(item.id, !!checked)}
                  aria-label="Select this item to change its date"
                  title="Select this item to change its date"
                />
                <span className="text-[9px] uppercase tracking-wide text-primary">Move</span>
              </div>
            )}
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing hidden sm:block"
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
            <Checkbox
              checked={isIncluded}
              onCheckedChange={(checked) => onToggleInclude(item.id, !!checked)}
              disabled={isCancelled || isReadOnly}
              title={isIncluded ? "Included in quote" : "Excluded from quote"}
            />
          </div>


          <div className="flex-1 min-w-0 space-y-2 sm:space-y-3">
            <div className="flex items-start justify-between gap-2 sm:gap-3">
              <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                <div className={`p-1.5 sm:p-2 rounded-lg border flex-shrink-0 ${ITEM_COLORS[item.item_type]}`}>
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                  {/* Standardized Title - Always Primary */}
                  <h4 className="font-bold text-base sm:text-lg text-foreground leading-tight mb-1 truncate">
                    {item.custom_title || item.title}
                    {isStaffRoom && (
                      <Badge variant="secondary" className="ml-2 bg-slate-500/15 text-slate-700 border-slate-500/30 text-[10px] uppercase align-middle">
                        Staff rooms
                        {item.staff_role_tags && item.staff_role_tags.length > 0 && (
                          <span className="ml-1 normal-case font-normal">
                            ({item.staff_role_tags.join(", ")})
                          </span>
                        )}
                      </Badge>
                    )}
                  </h4>
                  {/* Standardized Description - Always Secondary */}
                  {(() => {
                    const rawDesc = item.custom_description || item.description || '';
                    const displayDescription = isRichHtml(rawDesc) ? rawDesc.replace(/<\/(p|li|h2)>/g, ' ').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim() : rawDesc;
                    return (displayDescription || item.supplier) && (
                      <p className="font-normal text-xs sm:text-sm text-muted-foreground line-clamp-2 sm:line-clamp-3">
                        {displayDescription}
                        {item.supplier && !displayDescription && `by ${item.supplier}`}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <Badge variant="outline" className="capitalize text-xs flex-shrink-0 hidden sm:inline-flex">
                {(item as any).category_id && categoryNames?.[(item as any).category_id]
                  ? categoryNames[(item as any).category_id]
                  : item.item_type}
              </Badge>
              {pricingTreatment(item as any) === "optional" && (
                <Badge variant="secondary" className="text-xs">Optional extra · not in total</Badge>
              )}
              {pricingTreatment(item as any) === "alternative" && (
                <Badge variant="secondary" className="text-xs">Alternative option · not in total</Badge>
              )}
              {linkedTitle && (
                <Badge variant="outline" className="text-xs">Goes with: {linkedTitle}</Badge>
              )}
              {(item as any).markup_percentage !== null && (item as any).markup_percentage !== undefined && (
                <Badge variant="outline" className="text-xs">
                  Custom markup: {(item as any).markup_percentage}%
                </Badge>
              )}

            </div>

            <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 border ${isBackground ? 'border-amber-500/40 bg-amber-500/10' : 'border-border bg-muted/30'}`}>
              <Switch
                id={`bg-${item.id}`}
                checked={isBackground}
                onCheckedChange={(checked) => onToggleBackground(item.id, !!checked)}
                disabled={isCancelled || isReadOnly}
                className="scale-75 -ml-1"
              />
              <label
                htmlFor={`bg-${item.id}`}
                className={`text-[11px] uppercase tracking-wide font-medium cursor-pointer ${isBackground ? 'text-amber-700' : 'text-muted-foreground'}`}
                title="Hide this item from the itinerary on every document. Its cost is still included in the total."
              >
                Background cost{isBackground ? ' — hidden from itinerary' : ''}
              </label>
            </div>

            <div className="flex items-center gap-2 rounded-md px-2 py-1.5 border border-border bg-muted/30">
              <Switch
                id={`trade-discount-${item.id}`}
                checked={item.exclude_from_trade_discount === true}
                onCheckedChange={(checked) => onToggleTradeDiscountExclusion(item.id, !!checked)}
                disabled={isCancelled || isReadOnly}
                className="scale-75 -ml-1"
              />
              <label
                htmlFor={`trade-discount-${item.id}`}
                className="text-[11px] uppercase tracking-wide font-medium cursor-pointer text-muted-foreground"
                title="Keep this item's full value when a trade discount is applied to the quote."
              >
                Exclude from trade discount{item.exclude_from_trade_discount === true ? ' — yes' : ''}
              </label>
            </div>

            {leaderActive && item.item_type !== 'accommodation' && item.room_guest_type !== 'staff' && (
              <div className="flex items-center gap-2 rounded-md px-2 py-1.5 border border-border bg-muted/30">
                <Switch
                  id={`leader-${item.id}`}
                  checked={item.leader_excluded !== true}
                  onCheckedChange={(checked) => onToggleLeaderJoins?.(item.id, !!checked)}
                  disabled={isCancelled || isReadOnly}
                  className="scale-75 -ml-1"
                />
                <label
                  htmlFor={`leader-${item.id}`}
                  className="text-[11px] uppercase tracking-wide font-medium cursor-pointer text-muted-foreground"
                  title="Whether the group leader takes part in this item, and so whether their cost is added here."
                >
                  Group leader joins{item.leader_excluded === true ? ' — no' : ''}
                </label>
              </div>
            )}


            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
              {(() => {
                // For accommodation, display check_in date; for others, use date
                const displayDate = item.item_type === 'accommodation' ? item.check_in : item.date;
                return displayDate && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(displayDate), "MMM d, yyyy")}</span>
                  </div>
                );
              })()}
              {item.item_type !== 'accommodation' && (() => {
                const anyItem = item as any;
                const slot = anyItem.selected_time_slot || {};
                const t = (v?: string | null) => (v ? String(v).slice(0, 5) : "");
                const start = t(anyItem.override_start_time) || t(slot.start);
                const end = t(anyItem.override_end_time) || t(slot.end);
                if (!start) return null;
                return (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{end ? `${start} – ${end}` : start}</span>
                  </div>
                );
              })()}
              {item.location && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{item.location}</span>
                </div>
              )}
              {item.supplier && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{item.supplier}</span>
                </div>
              )}
              {item.website_url && (() => {
                const raw = item.website_url.trim();
                const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 text-primary hover:underline min-w-0"
                  >
                    <Globe className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{raw.replace(/^https?:\/\//i, "").replace(/\/$/, "")}</span>
                  </a>
                );
              })()}
              {totalCost > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CurrencyIcon className="h-4 w-4" />
                  <span>
                    {currencySymbol}{totalCost.toLocaleString()}
                    {item.item_type === 'accommodation' && nights > 0 && (allocationSummary((item as any).room_allocations) ? (
                      <span className="text-xs ml-1">
                        ({nights} night{nights > 1 ? 's' : ''} · {allocationSummary((item as any).room_allocations)})
                      </span>
                    ) : (
                      <span className="text-xs ml-1">
                        ({nights} night{nights > 1 ? 's' : ''} × {currencySymbol}{(item.cost || 0).toLocaleString()}
                        {item.occupancy_type && ` (${item.occupancy_type.charAt(0).toUpperCase() + item.occupancy_type.slice(1)})`}
                        {(item.quantity || 1) > 1 ? ` × ${item.quantity} rooms` : ''})
                      </span>
                    )
                    )}
                    {item.item_type !== 'custom' && item.item_type !== 'accommodation' && (item.quantity || 1) > 1 && (
                      <span className="text-xs ml-1">
                        ({item.quantity} × {currencySymbol}{(item.cost || 0).toLocaleString()})
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {item.notes && (
              <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                <span className="font-medium">Notes:</span> {item.notes}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={isCancelled || isReadOnly}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(item)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate(item)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                {isGuestAccommodation && (
                  <DropdownMenuItem onClick={() => onAddStaffRooms(item)}>
                    <Hotel className="h-4 w-4 mr-2" />
                    Add staff rooms
                  </DropdownMenuItem>
                )}
                {item.item_type === "custom" && onSaveToInventory && (
                  <DropdownMenuItem onClick={() => onSaveToInventory(item)}>
                    <Archive className="h-4 w-4 mr-2" />
                    {savedAs ? `Save for reuse again (saved as ${savedAs})` : "Save for reuse…"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => item.onRequestQuote?.(item.id)}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request Quote
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(item.id)} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Change Log */}
        {changes.length > 0 && (
          <Collapsible className="mt-3">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  View Change History ({changes.length})
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              {changes.map((change: any) => (
                <div key={change.id} className="text-xs bg-muted p-3 rounded-md space-y-1">
                  <div className="font-medium text-foreground">
                    {format(new Date(change.change_date), "MMM dd, yyyy 'at' HH:mm")}
                  </div>
                  <div className="text-muted-foreground">{change.change_source}</div>
                  {change.field_changed && (
                    <div className="text-muted-foreground">
                      <span className="font-medium">{change.field_changed}:</span>{" "}
                      <span className="line-through">{change.old_value}</span> → {change.new_value}
                    </div>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </Card>
    </div>
  );
}

export const ItineraryList = ({ items, leadId, currency = "ZAR", paxCount = 1, leadStatus, leadStartDate }: ItineraryListProps) => {
  const queryClient = useQueryClient();
  const [sortedItems, setSortedItems] = useState(items);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<ItineraryItem | null>(null);
  const [inventoryItem, setInventoryItem] = useState<ItineraryItem | null>(null);
  // Custom items already saved for reuse on this lead -> "product" / "supplier".
  const customItemIds = (items || []).filter((i: any) => i.item_type === "custom").map((i: any) => i.id as string);
  const { data: savedInventory } = useQuery({
    queryKey: ["inventory-saved-items", leadId, customItemIds.join(",")],
    enabled: customItemIds.length > 0,
    queryFn: async () => {
      const [p, s] = await Promise.all([
        supabase.from("products").select("created_from_item_id").in("created_from_item_id", customItemIds),
        supabase.from("suppliers").select("created_from_item_id").in("created_from_item_id", customItemIds),
      ]);
      const map: Record<string, string> = {};
      (s.data || []).forEach((r: any) => { if (r.created_from_item_id) map[r.created_from_item_id] = "supplier"; });
      (p.data || []).forEach((r: any) => { if (r.created_from_item_id) map[r.created_from_item_id] = "product"; });
      return map;
    },
  });
  const [displayCurrency, setDisplayCurrency] = useState<string>(currency);
  const [quoteRequestDialog, setQuoteRequestDialog] = useState<{ open: boolean; itemId?: string }>({ open: false });
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newSectionName, setNewSectionName] = useState("");
  const { data: exchangeRates } = useExchangeRates();
  const isCancelled = leadStatus === 'cancelled';
  const isReadOnly = leadStatus === 'quote_accepted';
  const canBatchEdit = !isCancelled && !isReadOnly;
  const startDateDefault = useDatePickerDefault(leadStartDate);

  const toggleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => (selected ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  // --- Realign programme days -------------------------------------------
  const [realignOpen, setRealignOpen] = useState(false);

  const { data: leadDates } = useQuery({
    queryKey: ["lead-travel-dates", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("start_date, end_date, itinerary_layout, itinerary_sections")
        .eq("id", leadId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const alignment = analyseProgrammeAlignment(
    items as any,
    leadDates?.start_date ?? leadStartDate,
    leadDates?.end_date
  );

  const itineraryLayout = (leadDates?.itinerary_layout || "day_by_day") as ItineraryLayout;
  const itinerarySections = parseItinerarySections(leadDates?.itinerary_sections);

  const updateLayoutMutation = useMutation({
    mutationFn: async (layout: ItineraryLayout) => {
      const { error } = await supabase.from("leads").update({ itinerary_layout: layout }).eq("id", leadId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-travel-dates", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    },
    onError: () => toast.error("Couldn't change the itinerary layout"),
  });

  const saveSectionsMutation = useMutation({
    mutationFn: async (sections: string[]) => {
      const { error } = await supabase.from("leads").update({ itinerary_sections: sections }).eq("id", leadId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-travel-dates", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      setNewSectionName("");
    },
    onError: () => toast.error("Couldn't save that section"),
  });

  const addSection = () => {
    const name = newSectionName.trim();
    if (!name) return;
    const next = [...new Set([...itinerarySections, name])];
    saveSectionsMutation.mutate(next);
  };

  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingSectionValue, setEditingSectionValue] = useState("");

  const renameSectionMutation = useMutation({
    mutationFn: async ({ oldName, newName, defaultedIds }: { oldName: string; newName: string; defaultedIds: string[] }) => {
      const base = itinerarySections.includes(oldName) ? itinerarySections : [...itinerarySections, oldName];
      const next = [...new Set(base.map((s) => (s === oldName ? newName : s)))];
      const { error: e1 } = await supabase.from("leads").update({ itinerary_sections: next }).eq("id", leadId);
      if (e1) throw e1;
      const { error: e2 } = await (supabase as any)
        .from("itinerary_items")
        .update({ section_name: newName } as any)
        .eq("lead_id", leadId)
        .eq("section_name", oldName);
      if (e2) throw e2;
      if (defaultedIds.length) {
        const { error: e3 } = await (supabase as any)
          .from("itinerary_items")
          .update({ section_name: newName } as any)
          .in("id", defaultedIds);
        if (e3) throw e3;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-travel-dates", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries();
      setEditingSection(null);
      toast.success("Section renamed");
    },
    onError: () => toast.error("Couldn't rename that section"),
  });

  const commitSectionRename = (oldName: string, items: any[]) => {
    const newName = editingSectionValue.trim().slice(0, 100);
    if (!newName || newName === oldName) return setEditingSection(null);
    const defaultedIds = items.filter((i) => !i.section_name?.trim()).map((i) => i.id);
    renameSectionMutation.mutate({ oldName, newName, defaultedIds });
  };




  // Currency conversion function
  const convertCost = (amount: number, fromCurrency: string, toCurrency: string): number => {
    if (fromCurrency === toCurrency) return amount;
    if (!exchangeRates) return amount;
    
    // Convert to ZAR first (our base currency)
    let amountInZAR = amount;
    if (fromCurrency === "USD") {
      amountInZAR = amount * exchangeRates.USD_to_ZAR;
    } else if (fromCurrency === "EUR") {
      amountInZAR = amount * exchangeRates.EUR_to_ZAR;
    }
    
    // Convert from ZAR to target currency
    if (toCurrency === "ZAR") return amountInZAR;
    if (toCurrency === "USD") return amountInZAR / exchangeRates.USD_to_ZAR;
    if (toCurrency === "EUR") return amountInZAR / exchangeRates.EUR_to_ZAR;
    
    return amount;
  };

  // Lead pricing context for the single supplement / group leader calculators
  const { data: leadPricing } = useQuery({
    queryKey: ["lead-supplement-context", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("markup_percentage, single_supplement_amount, group_leader_count, group_leader_room_overrides")
        .eq("id", leadId)
        .maybeSingle();
      if (error) throw error;
      return data as {
        markup_percentage: number | null;
        single_supplement_amount: number | null;
        group_leader_count: number | null;
        group_leader_room_overrides: unknown;
      } | null;
    },
  });

  const leaderCount = Number((leadPricing as any)?.group_leader_count || 0);
  const leaderRoomOverrides = parseLeaderRoomOverrides((leadPricing as any)?.group_leader_room_overrides);
  const leaderActive = leaderCount > 0;

  const toggleLeaderJoinsMutation = useMutation({
    mutationFn: async ({ id, joins }: { id: string; joins: boolean }) => {
      const { error } = await supabase
        .from("itinerary_items")
        .update({ leader_excluded: !joins } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
    },
    onError: (error) => {
      toast.error("Failed to update the group leader setting for this item");
      console.error(error);
    },
  });

  const handleToggleLeaderJoins = (id: string, joins: boolean) => {
    setSortedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, leader_excluded: !joins } : item))
    );
    toggleLeaderJoinsMutation.mutate({ id, joins });
  };


  // Fetch changes for all items
  const { data: allChanges } = useQuery({
    queryKey: ["itinerary-changes", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_changes")
        .select("*")
        .in("itinerary_item_id", items.map(i => i.id))
        .order("change_date", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: items.length > 0,
  });

  // Update sorted items when items prop changes
  useEffect(() => {
    setSortedItems(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateOrderMutation = useMutation({
    mutationFn: async (items: ItineraryItem[]) => {
      const updates = items.map((item, index) => ({
        id: item.id,
        sort_order: index,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from("itinerary_items")
          .update({ sort_order: update.sort_order })
          .eq("id", update.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Order updated");
    },
    onError: (error) => {
      toast.error("Failed to update order");
      console.error(error);
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("itinerary_items")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Item deleted");
      setDeleteId(null);
    },
    onError: (error) => {
      toast.error("Failed to delete item");
      console.error(error);
    },
  });

  const toggleIncludeMutation = useMutation({
    mutationFn: async ({ id, include }: { id: string; include: boolean }) => {
      const { error } = await supabase
        .from("itinerary_items")
        .update({ include_in_quote: include })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
    },
    onError: (error) => {
      toast.error("Failed to update item");
      console.error(error);
    },
  });

  const handleToggleInclude = (id: string, include: boolean) => {
    // Optimistically update local state
    setSortedItems(prev => prev.map(item => 
      item.id === id ? { ...item, include_in_quote: include } : item
    ));
    toggleIncludeMutation.mutate({ id, include });
  };

  const toggleBackgroundMutation = useMutation({
    mutationFn: async ({ id, isBackground }: { id: string; isBackground: boolean }) => {
      const { error } = await supabase
        .from("itinerary_items")
        .update({ is_background_cost: isBackground })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
    },
    onError: (error) => {
      toast.error("Failed to update background cost");
      console.error(error);
    },
  });

  const handleToggleBackground = (id: string, isBackground: boolean) => {
    setSortedItems(prev => prev.map(item =>
      item.id === id ? { ...item, is_background_cost: isBackground } : item
    ));
    toggleBackgroundMutation.mutate({ id, isBackground });
  };

  const toggleTradeDiscountExclusionMutation = useMutation({
    mutationFn: async ({ id, excluded }: { id: string; excluded: boolean }) => {
      const { error } = await supabase
        .from("itinerary_items")
        .update({ exclude_from_trade_discount: excluded })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
    },
    onError: (error) => {
      toast.error("Failed to update the trade discount setting");
      console.error(error);
    },
  });

  const handleToggleTradeDiscountExclusion = (id: string, excluded: boolean) => {
    setSortedItems((prev) => prev.map((item) =>
      item.id === id ? { ...item, exclude_from_trade_discount: excluded } : item
    ));
    toggleTradeDiscountExclusionMutation.mutate({ id, excluded });
  };

  const addStaffRoomsMutation = useMutation({
    mutationFn: async (item: ItineraryItem) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: maxOrderData } = await supabase
        .from("itinerary_items")
        .select("sort_order")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .single();

      const nextSortOrder = (maxOrderData?.sort_order ?? -1) + 1;

      const staffRow = {
        lead_id: leadId,
        user_id: user.id,
        item_type: 'accommodation' as const,
        title: item.custom_title || item.title,
        custom_title: null,
        custom_description: null,
        description: item.description,
        supplier: item.supplier,
        date: item.check_in,
        check_in: item.check_in,
        check_out: item.check_out,
        accommodation_type: item.accommodation_type,
        location: item.location,
        cost: item.cost,
        manual_price: null,
        quantity: 1,
        notes: null,
        sort_order: nextSortOrder,
        occupancy_type: 'single',
        room_rate_id: item.room_rate_id,
        include_in_quote: item.include_in_quote ?? true,
        is_background_cost: true,
        room_guest_type: 'staff',
        staff_role_tags: null,
      };

      const { error } = await supabase.from("itinerary_items").insert([staffRow]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Staff rooms added — edit to set occupancy, quantity and role tags.");
    },
    onError: (error) => {
      toast.error("Failed to add staff rooms");
      console.error(error);
    },
  });

  const handleAddStaffRooms = (item: ItineraryItem) => {
    addStaffRoomsMutation.mutate(item);
  };

  const duplicateItemMutation = useMutation({
    mutationFn: async (item: ItineraryItem) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Calculate next sort_order
      const { data: maxOrderData } = await supabase
        .from("itinerary_items")
        .select("sort_order")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .single();

      const nextSortOrder = (maxOrderData?.sort_order ?? -1) + 1;

      // Create duplicate with all fields except id
      const duplicatedItem = copyItineraryItem(item as any, {
        lead_id: leadId,
        user_id: user.id,
        title: item.custom_title || item.title,
        custom_title: item.custom_title ? `${item.custom_title} (Copy)` : null,
        sort_order: nextSortOrder,
        room_guest_type: (item as any).room_guest_type ?? 'guest',
      });

      const { error } = await supabase
        .from("itinerary_items")
        .insert([duplicatedItem as any]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Item duplicated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to duplicate item");
      console.error(error);
    },
  });

  const handleDuplicate = (item: ItineraryItem) => {
    duplicateItemMutation.mutate(item);
  };

  // Batch date change for the items ticked in "Select items" mode.
  const batchDateMutation = useMutation({
    mutationFn: async (
      updates: { id: string; date: string; isAccommodation: boolean; checkIn: string | null; checkOut: string | null }[]
    ) => {
      for (const u of updates) {
        const updateData: Record<string, unknown> = { date: u.date };
        if (u.isAccommodation) {
          const nights =
            u.checkIn && u.checkOut ? calculateAccommodationNights(u.checkIn, u.checkOut) : 0;
          updateData.check_in = u.date;
          updateData.check_out = format(addDays(parseISO(u.date), nights), "yyyy-MM-dd");
        }
        const { error } = await supabase
          .from("itinerary_items")
          .update(updateData)
          .eq("id", u.id);
        if (error) throw error;
      }
      return updates.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success(`${count} item${count === 1 ? "" : "s"} moved`);
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error("Failed to change the dates");
      console.error(error);
    },
  });

  const selectedItems = sortedItems.filter((i) => selectedIds.includes(i.id));

  // Realign the programme's travel dates (or shift every item earlier).
  const realignMutation = useMutation({
    mutationFn: async ({ choice, trimEnd }: { choice: RealignChoice; trimEnd: boolean }) => {
      const oldStart = leadDates?.start_date ?? leadStartDate ?? null;
      const oldEnd = leadDates?.end_date ?? null;
      const leadUpdate: Record<string, unknown> = {};

      if (choice === "move_start" && alignment.leadingEmptyDays > 0 && alignment.firstItemDate) {
        leadUpdate.start_date = alignment.firstItemDate;
      }
      if (trimEnd && alignment.trailingEmptyDays > 0 && alignment.lastItemDate) {
        leadUpdate.end_date = alignment.lastItemDate;
      }

      if (choice === "shift_items" && alignment.leadingEmptyDays > 0) {
        const days = -alignment.leadingEmptyDays;
        for (const item of sortedItems) {
          const isAccommodation = item.item_type === "accommodation";
          const base = isAccommodation ? item.check_in : item.date;
          if (!base) continue;
          const newDate = format(addDays(parseISO(base.split("T")[0]), days), "yyyy-MM-dd");
          const updateData: Record<string, unknown> = { date: newDate };
          if (isAccommodation) {
            const nights =
              item.check_in && item.check_out
                ? calculateAccommodationNights(item.check_in, item.check_out)
                : 0;
            updateData.check_in = newDate;
            updateData.check_out = format(addDays(parseISO(newDate), nights), "yyyy-MM-dd");
          }
          const { error } = await supabase
            .from("itinerary_items")
            .update(updateData)
            .eq("id", item.id);
          if (error) throw error;
        }
      }

      if (Object.keys(leadUpdate).length > 0) {
        const { error } = await supabase.from("leads").update(leadUpdate).eq("id", leadId);
        if (error) throw error;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const newStart = (leadUpdate.start_date as string) ?? oldStart;
        const newEnd = (leadUpdate.end_date as string) ?? oldEnd;
        await supabase.from("activity_log").insert({
          user_id: user.id,
          lead_id: leadId,
          action: "REALIGN_PROGRAMME_DAYS",
          details:
            choice === "move_start"
              ? `Realigned programme days by moving travel dates from ${oldStart ?? "-"} – ${oldEnd ?? "-"} to ${newStart ?? "-"} – ${newEnd ?? "-"}.`
              : `Realigned programme days by shifting all items ${alignment.leadingEmptyDays} day(s) earlier. Travel dates ${newStart ?? "-"} – ${newEnd ?? "-"}.`,
        });
      }

      return choice;
    },
    onSuccess: (choice) => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-travel-dates", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      setRealignOpen(false);
      toast.success(
        choice === "move_start"
          ? `Day 1 is now ${alignment.firstItemDate ? formatProgrammeDate(alignment.firstItemDate) : "the first day with items"}`
          : "Programme items moved earlier"
      );
    },
    onError: (error) => {
      toast.error("Failed to realign the programme days");
      console.error(error);
    },
  });

  const handleBatchSetDate = (newDate: Date) => {
    const dateStr = format(newDate, "yyyy-MM-dd");
    const updates = selectedItems.map((item) => ({
      id: item.id,
      date: dateStr,
      isAccommodation: item.item_type === "accommodation",
      checkIn: item.check_in,
      checkOut: item.check_out,
    }));
    if (updates.length === 0) return;
    batchDateMutation.mutate(updates);
  };

  const handleBatchShift = (days: number) => {
    const updates: {
      id: string;
      date: string;
      isAccommodation: boolean;
      checkIn: string | null;
      checkOut: string | null;
    }[] = [];
    let skipped = 0;

    selectedItems.forEach((item) => {
      const isAccommodation = item.item_type === "accommodation";
      const base = isAccommodation ? item.check_in : item.date;
      if (!base) {
        skipped += 1;
        return;
      }
      updates.push({
        id: item.id,
        date: format(addDays(parseISO(base.split("T")[0]), days), "yyyy-MM-dd"),
        isAccommodation,
        checkIn: item.check_in,
        checkOut: item.check_out,
      });
    });

    if (skipped > 0) {
      toast.info(
        `${skipped} selected item${skipped === 1 ? " has" : "s have"} no date yet, so ${
          skipped === 1 ? "it was" : "they were"
        } left alone. Use "Set date" for those.`
      );
    }
    if (updates.length === 0) return;
    batchDateMutation.mutate(updates);
  };



  // Mutation for updating both sort order and date when dragging across days
  const updateItemWithDateMutation = useMutation({
    mutationFn: async ({ 
      items, 
      movedItemId, 
      newDate, 
      isAccommodation,
      originalCheckIn,
      originalCheckOut 
    }: {
      items: ItineraryItem[];
      movedItemId: string;
      newDate: string | null;
      isAccommodation: boolean;
      originalCheckIn: string | null;
      originalCheckOut: string | null;
    }) => {
      // Update sort_order for all items
      for (const [index, item] of items.entries()) {
        const updateData: Record<string, unknown> = { sort_order: index };
        
        // For the moved item, also update the date
        if (item.id === movedItemId && newDate) {
          updateData.date = newDate;
          
          // For accommodation, update check_in and recalculate check_out to preserve nights
          if (isAccommodation && originalCheckIn && originalCheckOut) {
            const nights = calculateAccommodationNights(originalCheckIn, originalCheckOut);
            const newCheckOut = addDays(parseISO(newDate), nights);
            updateData.check_in = newDate;
            updateData.check_out = format(newCheckOut, 'yyyy-MM-dd');
          }
        }
        
        const { error } = await supabase
          .from("itinerary_items")
          .update(updateData)
          .eq("id", item.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Item moved and date updated");
    },
    onError: (error) => {
      toast.error("Failed to move item");
      console.error(error);
      // Revert to original order on error
      setSortedItems(items);
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    // Prevent reordering if cancelled or read-only
    if (isCancelled || isReadOnly) return;
    
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);
    const draggedItem = sortedItems[oldIndex];
    const targetItem = sortedItems[newIndex];
    
    // Determine the target date from the item at the drop position
    // For accommodation, use check_in date; for others, use date
    const targetDate = targetItem.item_type === 'accommodation' 
      ? targetItem.check_in 
      : targetItem.date;

    // Reorder in the UI immediately
    const newItems = arrayMove(sortedItems, oldIndex, newIndex);
    setSortedItems(newItems);

    // Check if the date is actually changing
    const draggedItemDate = draggedItem.item_type === 'accommodation' 
      ? draggedItem.check_in 
      : draggedItem.date;
    
    const isDateChanging = targetDate && draggedItemDate !== targetDate;

    if (isDateChanging) {
      // Update database with both order and date
      updateItemWithDateMutation.mutate({
        items: newItems,
        movedItemId: draggedItem.id,
        newDate: targetDate,
        isAccommodation: draggedItem.item_type === 'accommodation',
        originalCheckIn: draggedItem.check_in,
        originalCheckOut: draggedItem.check_out,
      });
    } else {
      // Just update sort order (same date, or no date available)
      updateOrderMutation.mutate(newItems);
    }
  };

  if (sortedItems.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ActivityIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No itinerary items yet. Add your first item to get started!</p>
      </div>
    );
  }

  // Calculate totals in lead's base currency - separate included vs excluded
  // Uses shared utility that correctly handles accommodation nights
  const {
    includedTotal,
    excludedTotal,
    includedCount,
    excludedCount,
    includedAccommodation,
    includedOther,
  } = sortedItems.reduce(
    (acc, item) => {
      const itemCost = baseItemCost(item);
      
      if (item.include_in_quote !== false) {
        const isAccommodation = item.item_type === 'accommodation';
        return { 
          ...acc, 
          includedTotal: acc.includedTotal + itemCost,
          includedCount: acc.includedCount + 1,
          includedAccommodation: acc.includedAccommodation + (isAccommodation ? itemCost : 0),
          includedOther: acc.includedOther + (isAccommodation ? 0 : itemCost),
        };
      } else {
        return { 
          ...acc, 
          excludedTotal: acc.excludedTotal + itemCost,
          excludedCount: acc.excludedCount + 1
        };
      }
    },
    {
      includedTotal: 0,
      excludedTotal: 0,
      includedCount: 0,
      excludedCount: 0,
      includedAccommodation: 0,
      includedOther: 0,
    }
  );

  // Group leader overlay (nothing is written to the items)
  const leaderImpact = computeGroupLeaderImpact(sortedItems as any, {
    payingPax: paxCount,
    leaderCount,
    roomOverrides: leaderRoomOverrides,
  });

  // Convert totals to display currency
  const includedCostDisplay = convertCost(includedTotal + leaderImpact.netTotal, currency, displayCurrency);
  const excludedCostDisplay = convertCost(excludedTotal, currency, displayCurrency);
  const accommodationCostDisplay = convertCost(includedAccommodation + leaderImpact.accommodationTotal, currency, displayCurrency);
  const otherCostDisplay = convertCost(includedOther + leaderImpact.otherTotal, currency, displayCurrency);
  const leaderCostDisplay = convertCost(leaderImpact.netTotal, currency, displayCurrency);
  const perPersonCost = includedCostDisplay / paxCount;
  const displayCurrencySymbol = getCurrencySymbol(displayCurrency);
  const Eq = ({ v }: { v: number }) => {
    const t = v > 0 ? formatEquivalents(v, displayCurrency, exchangeRates) : null;
    return t ? <span className="block text-[11px] font-normal text-muted-foreground">{t}</span> : null;
  };
  const fmtMoney = (v: number) =>
    `${displayCurrencySymbol}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


  return (
    <>
      <div className="space-y-4">
        <div className="p-4 bg-card rounded-lg border space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-foreground">Programme Summary</h3>
            <div className="flex flex-wrap items-center gap-2">
            {canBatchEdit && (
              <Button
                variant={selectMode ? "secondary" : "outline"}
                size="sm"
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              >
                <CheckSquare className="mr-2 h-4 w-4" />
                {selectMode ? "Exit select mode" : "Select items"}
              </Button>
            )}

            {canBatchEdit && alignment.needsRealign && (
              <Button variant="outline" size="sm" onClick={() => setRealignOpen(true)}>
                <CalendarClock className="mr-2 h-4 w-4" />
                Realign days
              </Button>
            )}


            <GroupLeaderDialog
              leadId={leadId}
              items={sortedItems as any}
              currency={currency}
              payingPax={paxCount}
              savedLeaderCount={leaderCount || null}
              savedRoomOverrides={leaderRoomOverrides}
              disabled={isCancelled || isReadOnly}
            />
            <SingleSupplementDialog
              leadId={leadId}
              items={sortedItems as any}
              currency={currency}
              markupPercent={Number(leadPricing?.markup_percentage ?? 0)}
              savedAmount={leadPricing?.single_supplement_amount ?? null}
              disabled={isCancelled || isReadOnly}
            />

            <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="ZAR">ZAR (R)</SelectItem>
              </SelectContent>
            </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t pt-3">
            <span className="text-sm font-medium">Display</span>
            <div className="flex rounded-md border p-1">
              <Button
                type="button"
                size="sm"
                variant={itineraryLayout === "day_by_day" ? "secondary" : "ghost"}
                onClick={() => updateLayoutMutation.mutate("day_by_day")}
                disabled={!canBatchEdit}
              >
                Day-by-day
              </Button>
              <Button
                type="button"
                size="sm"
                variant={itineraryLayout === "sections" ? "secondary" : "ghost"}
                onClick={() => updateLayoutMutation.mutate("sections")}
                disabled={!canBatchEdit}
              >
                Programme sections
              </Button>
            </div>
            {itineraryLayout === "sections" && canBatchEdit && (
              <div className="flex min-w-64 flex-1 items-center gap-2">
                <Input
                  value={newSectionName}
                  onChange={(event) => setNewSectionName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addSection();
                    }
                  }}
                  placeholder="Add a programme section"
                />
                <Button type="button" variant="outline" onClick={addSection} disabled={!newSectionName.trim()}>
                  Add
                </Button>
              </div>
            )}
          </div>
          {displayCurrency !== currency && (
            <p className="text-xs text-muted-foreground">
              Showing in {displayCurrency} (Lead currency: {currency})
            </p>
          )}
          
          <div className="space-y-3 pt-3 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Included in Quote ({includedCount} items):
              </span>
              <span className="font-bold text-base text-primary">
                {displayCurrencySymbol}{includedCostDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <Eq v={includedCostDisplay} />
              </span>
            </div>

            {(accommodationCostDisplay > 0 || otherCostDisplay > 0) && (
              <div className="space-y-1 pl-3 border-l">
                {accommodationCostDisplay > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Accommodation</span>
                    <span className="text-right">
                      <span className="font-medium">{fmtMoney(accommodationCostDisplay)}</span><Eq v={accommodationCostDisplay} />
                      <span className="block text-muted-foreground">
                        {fmtMoney(accommodationCostDisplay / paxCount)} pp<Eq v={accommodationCostDisplay / paxCount} />
                      </span>
                    </span>
                  </div>
                )}
                {otherCostDisplay > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Activities &amp; Ground Handling</span>
                    <span className="text-right">
                      <span className="font-medium">{fmtMoney(otherCostDisplay)}</span><Eq v={otherCostDisplay} />
                      <span className="block text-muted-foreground">
                        {fmtMoney(otherCostDisplay / paxCount)} pp<Eq v={otherCostDisplay / paxCount} />
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {leaderActive && (
              <div className="flex justify-between text-xs rounded-md bg-primary/5 border border-primary/20 px-2 py-1.5">
                <span className="text-muted-foreground">
                  Includes {leaderCount} group leader{leaderCount === 1 ? "" : "s"} — cost absorbed
                </span>
                <span className="text-right">
                  <span className="font-medium">{fmtMoney(leaderCostDisplay)}</span><Eq v={leaderCostDisplay} />
                  <span className="block text-muted-foreground">
                    {fmtMoney(leaderCostDisplay / paxCount)} pp<Eq v={leaderCostDisplay / paxCount} />
                  </span>
                </span>
              </div>
            )}

            {excludedCount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Excluded from Quote ({excludedCount} items):
                </span>
                <span className="font-medium text-muted-foreground line-through">
                  {displayCurrencySymbol}{excludedCostDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <Eq v={excludedCostDisplay} />
                </span>
              </div>
            )}
            
            <div className="flex justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground">
                Per Person ({paxCount} paying{leaderActive ? ` + ${leaderCount} leader${leaderCount === 1 ? "" : "s"}` : ""}):
              </span>
              <span className="font-bold text-base">
                {displayCurrencySymbol}{perPersonCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <Eq v={perPersonCost} />
              </span>
            </div>

          </div>
          
          {exchangeRates?.fallback && (
            <p className="text-xs text-muted-foreground pt-2 border-t">
              * Exchange rates are fallback values. Live rates unavailable.
            </p>
          )}
        </div>

        {selectMode && canBatchEdit && (
          <BatchDateBar
            selectedCount={selectedIds.length}
            defaultMonth={startDateDefault}
            isSaving={batchDateMutation.isPending}
            onSetDate={handleBatchSetDate}
            onShift={handleBatchShift}
            onClear={() => setSelectedIds([])}
            onDone={exitSelectMode}
          />
        )}


        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedItems.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {(() => {
              const { infrastructure, dayItems: afterInfra } = splitInfrastructureFees(sortedItems);
              const { ongoing, dayItems } = splitOngoingProgrammes(afterInfra as any[]) as any;
              const infraSubtotalBase = infrastructure.reduce(
                (sum, it) => sum + (it.include_in_quote !== false ? baseItemCost(it) : 0),
                0
              );
              const infraSubtotal = convertCost(infraSubtotalBase, currency, displayCurrency);
              const ongoingSubtotalBase = (ongoing as any[]).reduce(
                (sum: number, it: any) => sum + (it.include_in_quote !== false ? baseItemCost(it) : 0),
                0
              );
              const ongoingSubtotal = convertCost(ongoingSubtotalBase, currency, displayCurrency);
              const renderRow = (item: any) => {
                const itemChanges =
                  allChanges?.filter((c) => c.itinerary_item_id === item.id) || [];
                return (
                  <SortableItem
                    key={item.id}
                    item={{
                      ...item,
                      onRequestQuote: (itemId: string) =>
                        setQuoteRequestDialog({ open: true, itemId }),
                    }}
                    onDelete={setDeleteId}
                    onEdit={setEditItem}
                    onDuplicate={handleDuplicate}
                    onToggleInclude={handleToggleInclude}
                    onToggleBackground={handleToggleBackground}
                    onToggleTradeDiscountExclusion={handleToggleTradeDiscountExclusion}
                    onAddStaffRooms={handleAddStaffRooms}
                    onSaveToInventory={setInventoryItem}
                    savedAs={savedInventory?.[item.id] ?? null}
                    changes={itemChanges}
                    isCancelled={isCancelled}
                    isReadOnly={isReadOnly}
                    currency={currency}
                    baseCurrency={currency}
                    displayCurrency={displayCurrency}
                    convertCost={convertCost}
                    leaderActive={leaderActive}
                    onToggleLeaderJoins={handleToggleLeaderJoins}
                    selectMode={selectMode && canBatchEdit}
                    isSelected={selectedIds.includes(item.id)}
                    onToggleSelect={toggleSelect}
                  />
                );
              };
              return (
                <>
                  {infrastructure.length > 0 && (

                    <div className="mb-2">
                      <div className="rounded-t-lg bg-secondary text-secondary-foreground px-4 py-3 flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-bold tracking-wide">INFRASTRUCTURE FEES</h3>
                          <p className="text-xs text-secondary-foreground/80">Programme-wide · {infrastructure.length} item{infrastructure.length === 1 ? '' : 's'}</p>
                        </div>
                        <div className="text-sm font-semibold">
                          {displayCurrencySymbol}{infraSubtotal.toFixed(2)}
                        </div>
                      </div>
                      <div className="border border-t-0 rounded-b-lg p-3 bg-card">
                        {infrastructure.map((item) => renderRow(item))}
                      </div>
                    </div>
                  )}
                  {itineraryLayout !== "sections" && (ongoing as any[]).length > 0 && (
                    <div className="mb-2">
                      <div className="rounded-t-lg bg-secondary text-secondary-foreground px-4 py-3 flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-bold tracking-wide">ACADEMIC &amp; ONGOING PROGRAMMES</h3>
                          <p className="text-xs text-secondary-foreground/80">
                            Runs across multiple days · {(ongoing as any[]).length} item{(ongoing as any[]).length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="text-sm font-semibold">
                          {displayCurrencySymbol}{ongoingSubtotal.toFixed(2)}
                        </div>
                      </div>
                      <div className="border border-t-0 rounded-b-lg p-3 bg-card space-y-2">
                        {(ongoing as any[]).map((item) => (
                          <div key={item.id}>
                            {renderRow(item)}
                            <p className="text-xs text-muted-foreground px-2 pb-1">
                              {[
                                formatSessionDateSpan(item),
                                formatSessionSummary(item),
                                formatSessionTiming(item),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(itineraryLayout === "sections"
                    ? groupItemsBySection([...(ongoing as any[]), ...(dayItems as any[])], itinerarySections)
                    : groupItemsByDay(dayItems as any[], leadStartDate)
                  ).map((group: any) => {

              const daySubtotalBase = group.items.reduce(
                (sum, it) =>
                  sum +
                  (it.include_in_quote !== false ? baseItemCost(it) : 0),
                0
              );
              const daySubtotal = convertCost(daySubtotalBase, currency, displayCurrency);
              return (
                <div key={itineraryLayout === "sections" ? `section-${group.name}` : `day-${group.dayNumber}`} className="mb-2">
                  {itineraryLayout === "sections" ? (
                    <div className="rounded-t-lg bg-secondary px-4 py-3 text-secondary-foreground">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          {editingSection === group.name ? (
                            <div className="flex items-center gap-1">
                              <Input
                                autoFocus
                                value={editingSectionValue}
                                maxLength={100}
                                onChange={(e) => setEditingSectionValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") commitSectionRename(group.name, group.items);
                                  if (e.key === "Escape") setEditingSection(null);
                                }}
                                className="h-8 max-w-sm bg-background text-foreground"
                              />
                              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Save section name" disabled={renameSectionMutation.isPending} onClick={() => commitSectionRename(group.name, group.items)}>
                                <CheckIcon className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Cancel rename" onClick={() => setEditingSection(null)}>
                                <XIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <h3 className="font-bold">{group.name}</h3>
                              {canBatchEdit && !isReadOnly && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Rename section" onClick={() => { setEditingSection(group.name); setEditingSectionValue(group.name); }}>
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-secondary-foreground/80">
                            {group.items.length} programme element{group.items.length === 1 ? "" : "s"}
                          </p>
                        </div>
                        <span className="text-sm font-semibold">{displayCurrencySymbol}{daySubtotal.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : (
                    <DayBanner
                      dayNumber={group.dayNumber}
                      date={group.date}
                      itemCount={group.items.length}
                      subtotal={daySubtotal}
                      currencySymbol={displayCurrencySymbol}
                    />
                  )}
                  <div className="border border-t-0 rounded-b-lg p-3 bg-card">
                    {selectMode && canBatchEdit && (
                      <div className="mb-2 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSelectedIds((prev) => [
                              ...new Set([...prev, ...group.items.map((i) => i.id)]),
                            ])
                          }
                        >
                            Select all in this {itineraryLayout === "sections" ? "section" : "day"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSelectedIds((prev) =>
                              prev.filter((id) => !group.items.some((i) => i.id === id))
                            )
                          }
                        >
                          Unselect {itineraryLayout === "sections" ? "section" : "day"}
                        </Button>
                      </div>
                    )}

                    {group.items.map((item) => {
                      const itemChanges =
                        allChanges?.filter((c) => c.itinerary_item_id === item.id) || [];
                      return (
                        <div key={item.id}>
                        <SortableItem
                          item={{
                            ...item,
                            onRequestQuote: (itemId: string) =>
                              setQuoteRequestDialog({ open: true, itemId }),
                          }}
                          onDelete={setDeleteId}
                          onEdit={setEditItem}
                          onDuplicate={handleDuplicate}
                          onToggleInclude={handleToggleInclude}
                          onToggleBackground={handleToggleBackground}
                          onToggleTradeDiscountExclusion={handleToggleTradeDiscountExclusion}
                          onAddStaffRooms={handleAddStaffRooms}
                    onSaveToInventory={setInventoryItem}
                    savedAs={savedInventory?.[item.id] ?? null}
                          changes={itemChanges}
                          isCancelled={isCancelled}
                          isReadOnly={isReadOnly}
                          currency={currency}
                          baseCurrency={currency}
                          displayCurrency={displayCurrency}
                          convertCost={convertCost}
                          leaderActive={leaderActive}
                          onToggleLeaderJoins={handleToggleLeaderJoins}
                          selectMode={selectMode && canBatchEdit}
                          isSelected={selectedIds.includes(item.id)}
                          onToggleSelect={toggleSelect}


                        />
                        {itineraryLayout === "sections" && (item.session_count || 0) > 1 && (
                          <p className="text-xs text-muted-foreground px-2 pb-1">
                            {[
                              formatSessionDateSpan(item),
                              formatSessionSummary(item),
                              formatSessionTiming(item),
                              totalSessionHours(item) ? `${totalSessionHours(item)} hours in total` : null,
                            ].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
                  })}
                </>
              );
            })()}
          </SortableContext>
        </DndContext>
      </div>

      <SaveToInventoryDialog
        item={inventoryItem as any}
        leadId={leadId}
        currency={currency}
        paxCount={paxCount}
        onClose={() => setInventoryItem(null)}
      />

      <EditItineraryItemDialog
        item={editItem}
        leadId={leadId}
        leadStatus={leadStatus}
        leadStartDate={leadStartDate}
        sectionOptions={[...new Set([...itinerarySections, ...DEFAULT_ITINERARY_SECTIONS])]}
        onClose={() => setEditItem(null)}
      />

      <RequestSupplierQuoteDialog
        open={quoteRequestDialog.open}
        onOpenChange={(open) => setQuoteRequestDialog({ open })}
        mode="single_item"
        leadId={leadId}
        itemId={quoteRequestDialog.itemId}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Itinerary Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteItemMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RealignDaysDialog
        open={realignOpen}
        onOpenChange={setRealignOpen}
        alignment={alignment}
        startDate={leadDates?.start_date ?? leadStartDate}
        endDate={leadDates?.end_date}
        isSaving={realignMutation.isPending}
        onConfirm={(choice, trimEnd) => realignMutation.mutate({ choice, trimEnd })}
      />

    </>
  );
};
